import React from 'react'
import { Space } from 'antd'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import { SpaceProps } from 'antd/es/space'
import cx from 'classnames'
import { spacing } from '../style/constants'
import './styles.scss'

const CommutifiSizeTypes = [
  'tiny',
  'tiny-2',
  'small-2',
  'small-3',
  'medium',
  'medium-2',
  'large-2',
  'large-3',
  'large-4'
] as const
export declare type CommutifiSizeType = (typeof CommutifiSizeTypes)[number]

type ExtendedSizeType = SizeType | CommutifiSizeType

const CommutifiAlignTypes = ['stretch'] as const
export declare type CommutifiAlignType = (typeof CommutifiAlignTypes)[number]
type ExtendedAlignType = SpaceProps['align'] | CommutifiAlignType

export interface SpacingProps extends Omit<SpaceProps, 'size' | 'align'> {
  /**
   * Antd values (small, medium, large or any number)
   * Commutifi sizes to apply predefined and reused values (see scss variables for values):
   *   - tiny-2
   *   - small-2
   *   - small-3
   *   - medium-2
   *   - large-2
   *   - large-3
   *   - large-4
   */
  size?: ExtendedSizeType
  align?: ExtendedAlignType
}

const typeSafeIsAntdSize = (size?: ExtendedSizeType): size is SizeType =>
  CommutifiSizeTypes.findIndex((s) => s === size) === -1
const typeSafeIsAntdAlign = (align?: ExtendedAlignType): align is SpaceProps['align'] =>
  CommutifiAlignTypes.findIndex((a) => a === align) === -1

const SpacingClass = 'app-spacing'

/*
 * This class is a wrap around [antd Typography](https://ant.design/components/space/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * It is used to insure we are consistent in our use of spacing. It it flexible enough to apply any other
 * spacing values that we might need and that is not a pre-defined value
 */
export function AppSpacing({ className, size, align, ...spacingProps }: SpacingProps) {
  const spacingSize = typeSafeIsAntdSize(size)
    ? { size }
    : { size: parseInt(spacing[size.replace('-', '') as keyof typeof spacing]) }

  return (
    <Space
      {...spacingProps}
      {...spacingSize}
      align={typeSafeIsAntdAlign(align) ? align : undefined}
      className={cx(SpacingClass, !typeSafeIsAntdAlign(align) && `${SpacingClass}-align-stretch`, className)}
    />
  )
}

export default AppSpacing
